import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { API_URL } from '../config';

// Fix for default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function ChangeMapView({ center }) {
    const map = useMap();
    useEffect(() => {
        if (center && center[0] && center[1]) {
            map.setView(center, map.getZoom());
        }
    }, [center, map]);
    return null;
}

function LocationMarker({ position, onPositionChange }) {
    useMapEvents({
        click(e) {
            const { lat, lng } = e.latlng;
            onPositionChange([lat, lng]);
        },
    });
    return position === null ? null : <Marker position={position} />;
}

export default function LocationPicker({ latitude, longitude, onLocationSelect, height = '300px' }) {
    const defaultCenter = [-1.286389, 36.817223];

    const [position, setPosition] = useState(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [isLocating, setIsLocating] = useState(false);

    // Autocomplete state
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const debounceTimer = useRef(null);
    const wrapperRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced autocomplete fetch as user types
    const handleSearchInput = (e) => {
        const val = e.target.value;
        setSearchQuery(val);
        setShowDropdown(true);

        clearTimeout(debounceTimer.current);
        if (val.trim().length < 3) {
            setSuggestions([]);
            return;
        }

        debounceTimer.current = setTimeout(async () => {
            setIsFetching(true);
            try {
                let results = [];
                // 1. Try backend location search proxy first (fully CSP compliant & bypasses rate limits)
                try {
                    const proxyRes = await fetch(
                        `${API_URL}/api/v1/public/location-search?q=${encodeURIComponent(val)}&countrycodes=ke&limit=8`
                    );
                    if (proxyRes.ok) {
                        const json = await proxyRes.json();
                        if (json?.isOk && Array.isArray(json?.data)) {
                            results = json.data;
                        }
                    }
                } catch {
                    // Fallback to direct Nominatim if proxy is unreachable
                    try {
                        const directRes = await fetch(
                            `https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&q=${encodeURIComponent(val)}&limit=8&addressdetails=1`
                        );
                        if (directRes.ok) {
                            results = await directRes.json();
                        }
                    } catch {
                        results = [];
                    }
                }
                setSuggestions(Array.isArray(results) ? results : []);
            } catch {
                setSuggestions([]);
            } finally {
                setIsFetching(false);
            }
        }, 350);
    };

    // When user picks a suggestion from dropdown
    const handleSelectSuggestion = (item) => {
        const lat = parseFloat(item.lat);
        const lon = parseFloat(item.lon);
        const newPos = [lat, lon];
        setPosition(newPos);
        setMapCenter(newPos);
        onLocationSelect(lat, lon);
        setSearchQuery(item.display_name);
        setSuggestions([]);
        setShowDropdown(false);
    };

    // Fetch user's current GPS location
    const fetchCurrentLocation = () => {
        if ('geolocation' in navigator) {
            setIsLocating(true);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const userLocation = [pos.coords.latitude, pos.coords.longitude];
                    setMapCenter(userLocation);
                    setPosition(userLocation);
                    onLocationSelect(pos.coords.latitude, pos.coords.longitude);
                    setSearchQuery('');
                    setSuggestions([]);
                    setShowDropdown(false);
                    setIsLocating(false);
                },
                (error) => {
                    console.warn('Geolocation error:', error.message);
                    setIsLocating(false);
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        }
    };

    // Update position when latitude/longitude props change (editing existing address or resetting)
    useEffect(() => {
        if (latitude && longitude) {
            const lat = parseFloat(latitude);
            const lng = parseFloat(longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                const newPosition = [lat, lng];
                setPosition(newPosition);
                setMapCenter(newPosition);
            }
        } else {
            setPosition(null);
            setSearchQuery('');
            setSuggestions([]);
            setShowDropdown(false);
            fetchCurrentLocation();
        }
    }, [latitude, longitude]);

    const handlePositionChange = (newPosition) => {
        setPosition(newPosition);
        const [lat, lng] = newPosition;
        onLocationSelect(lat, lng);
        setSearchQuery('');
        setSuggestions([]);
        setShowDropdown(false);
    };

    return (
        <div>
            {/* Search with autocomplete */}
            <div ref={wrapperRef} style={{ position: 'relative', marginBottom: '8px' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                        <input
                            type="text"
                            placeholder='Search location in Kenya (e.g. "Westlands", "Moi Avenue", "Kilimani")'
                            value={searchQuery}
                            onChange={handleSearchInput}
                            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setShowDropdown(false);
                                if (e.key === 'Enter' && suggestions.length > 0) {
                                    e.preventDefault();
                                    handleSelectSuggestion(suggestions[0]);
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '8px 36px 8px 12px',
                                border: '1px solid #E0E0E0',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                            }}
                            autoComplete="off"
                        />
                        {searchQuery && !isFetching && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchQuery('');
                                    setSuggestions([]);
                                    setShowDropdown(false);
                                }}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: '#9ca3af',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '2px 6px',
                                }}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                        {isFetching && (
                            <div style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '16px',
                                height: '16px',
                                border: '2px solid #e5e7eb',
                                borderTopColor: '#2563eb',
                                borderRadius: '50%',
                                animation: 'spin 0.6s linear infinite',
                            }} />
                        )}
                    </div>

                {/* Suggestions dropdown */}
                {showDropdown && suggestions.length > 0 && (
                    <ul style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 2000,
                        listStyle: 'none',
                        margin: '4px 0 0 0',
                        padding: 0,
                        maxHeight: '220px',
                        overflowY: 'auto',
                    }}>
                        {suggestions.map((item, i) => (
                            <li
                                key={i}
                                onMouseDown={() => handleSelectSuggestion(item)}
                                style={{
                                    padding: '10px 14px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: '#374151',
                                    borderBottom: i < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'flex-start',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                                <span style={{ marginTop: '1px', flexShrink: 0 }}>📍</span>
                                <span style={{ lineHeight: '1.4' }}>{item.display_name}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {showDropdown && searchQuery.length >= 3 && !isFetching && suggestions.length === 0 && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        fontSize: '13px',
                        color: '#9ca3af',
                        marginTop: '4px',
                        zIndex: 2000,
                    }}>
                        No results found. Try a different search term.
                    </div>
                )}
            </div>

            {/* Map container */}
            <div style={{ position: 'relative', height, width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E0E0E0' }}>
                {/* GPS button */}
                <button
                    type="button"
                    onClick={fetchCurrentLocation}
                    disabled={isLocating}
                    title="Use My Current Location"
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 1000,
                        backgroundColor: 'white',
                        border: '2px solid rgba(0,0,0,0.2)',
                        borderRadius: '4px',
                        padding: '6px',
                        cursor: isLocating ? 'not-allowed' : 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '34px',
                        height: '34px',
                    }}
                >
                    {isLocating ? (
                        <span style={{ fontSize: '18px' }}>⏳</span>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="#444"><path d="M0 0h24v24H0z" fill="none"/><path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>
                    )}
                </button>

                <MapContainer center={defaultCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                    <ChangeMapView center={mapCenter} />
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationMarker position={position} onPositionChange={handlePositionChange} />
                </MapContainer>
            </div>
            <p style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                💡 Type to search, click "Use My Location", or tap the map to pin your exact address.
            </p>

            {/* CSS for spinner */}
            <style>{`@keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }`}</style>
        </div>
    );
}